import React, { memo } from "react"
import { Link } from "gatsby"

import { splitMetric } from "../utils/metric"

/**
 * The proof block for service pages — section 7.
 *
 * One component for all three pages that have proof, rather than three copies of
 * the markup, so the construction cannot drift the way the metric handling
 * already had: the offer template set a leading figure at $fs-stat and the
 * service pages rendered the same metric as a flat line of body text.
 *
 * THREE THINGS CHANGED HERE (FIX 2):
 *
 *  a) Every metric leads with its figure at $fs-stat. Where a metric has no
 *     figure at all, it is NOT given one — it renders below the grid as a note
 *     instead, so the row of stats stays uniform and nothing is invented. Which
 *     metrics those are is reported rather than quietly padded.
 *
 *  b) A CLIENT tag sits above the block: the word "Client" as a label, then the
 *     name. Previously the name sat on its own as an eyebrow, which read as a
 *     section heading rather than as an attribution.
 *
 *  c) The name links to its case study ONLY where that route exists and returns
 *     200 — verified over HTTP, not inferred from a filename. Where there is no
 *     case study the name renders as plain text. It is never linked to a client's
 *     own website.
 *
 * The client name and the figures are attributed content and are reproduced as
 * they stand.
 */

const ServiceProof = ({ client, path, metrics = [], heading }) => {
  const split = metrics.map(m => ({ raw: m, ...splitMetric(m) }))
  const stats = split.filter(m => m.value)
  const notes = split.filter(m => !m.value)

  return (
    <section className="sp-proof">
      <div className="sp-proof__inner">
        <span className="sp-tag">Proof</span>
        <h2 className="sp-h2">
          {heading || "What changed for a business like yours"}
        </h2>

        {/* The attribution. "Client" carries the grammar so the name does not
            have to pretend to be a heading. */}
        <p className="sp-proof__attrib">
          <span className="sp-proof__attriblabel">Client</span>
          {path ? (
            <Link to={path} className="sp-proof__clientlink">
              {client}
            </Link>
          ) : (
            <span className="sp-proof__client">{client}</span>
          )}
        </p>

        {stats.length > 0 && (
          <div className="sp-proof__grid" data-count={stats.length}>
            {stats.map(m => (
              <div className="sp-stat" key={m.raw}>
                <p className="sp-stat__value">{m.value}</p>
                <p className="sp-stat__label">{m.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* No numeral, no invented numeral. These sit under the grid as prose. */}
        {notes.length > 0 && (
          <ul className="sp-proof__notes">
            {notes.map(m => (
              <li className="sp-proof__note" key={m.raw}>
                {m.label}
              </li>
            ))}
          </ul>
        )}

        {path && (
          <p className="sp-proof__link">
            <Link to={path}>Read how {client} got there &rarr;</Link>
          </p>
        )}
      </div>
    </section>
  )
}

export default memo(ServiceProof)
